import { isEqual } from 'lodash' // or your preferred utility
import { mapGetters, mapActions } from 'vuex'
import { format } from 'date-fns'
import { Periods } from '../cookie-module-constants.js'
import { getDomains } from '../../../../../../shared/utils/api/cookie-module.js'
import { waitForLoad, brandOptions } from '../../../../../../shared/state/brands.js'

// Determine default period at module load time
let determinedDefaultPeriod = 2 // Default to "Last month"
if (Periods && Periods.LastMonth && typeof Periods.LastMonth !== 'undefined') {
	determinedDefaultPeriod = Periods.LastMonth
}

/**
 * Mixin for managing filter state, options, and related API calls.
 */
export const filterStateMixin = {
	props: {
		initialFilters: {
			type: Object,
			required: true
		},
		initialPalette: {
			type: String,
			required: true
		},
		availableBrands: {
			type: Array,
			default: () => []
		}
	},
	setup () {
		return {
			brandOptions,
			Periods
		}
	},
	data () {
		return {
			editableFilters: {},
			editablePalette: null,
			appliedFilters: {},
			appliedPalette: null,
			APP_DEFAULT_PERIOD: determinedDefaultPeriod,
			consentBanners: [
				{
					id: -1,
					name: 'All Consent Banners',
					brand: null
				}
			],
			domains: []
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		...mapGetters('dashboards', ['dashboards']),
		selectedPeriodText () {
			const period = this.periodOptions.find(p => p.value === this.editableFilters.periodValue)
			return period ? period.text : 'Unknown'
		},
		selectedWidgetText () {
			if (this.editableFilters.widgetValue === -1) return 'All Consent Banners'
			const widget = this.consentBanners.find(b => b.id === this.editableFilters.widgetValue)
			return widget ? widget.name : 'Selected Widget'
		},
		selectedBrandText () {
			if (!this.editableFilters.brandId) return 'All'
			const brand = this.availableBrands.find(b => b.value === this.editableFilters.brandId)
			return brand ? brand.text : 'Selected Brand'
		},
		selectedDomainText () {
			return this.editableFilters.domainValue || 'All'
		},
		periodOptions () {
			return [
				{ value: Periods.LastWeek, text: 'Last week' },
				{ value: Periods.LastMonth, text: 'Last month' },
				{ value: Periods.Last3Months, text: 'Last 3 months' },
				{ value: Periods.Last6Months, text: 'Last 6 months' },
				{ value: Periods.Last12Months, text: 'Last 12 months' },
				{ value: Periods.Custom, text: 'Custom' }
			]
		},
		widgetOptions () {
			const banners = this.consentBanners
				.map(({ id, name, brandID }) => ({
					value: id,
					text: name,
					brand: brandID
				}))
				.filter(x => x.brand === this.editableFilters.brandId || this.editableFilters.brandId == null || x.brand == null)

			const allWidgetsItem = banners.find(x => x.value === -1)
			const alphabeticalSort = banners.filter(x => x.value !== -1).sort((a, b) => a.text.localeCompare(b.text))
			return allWidgetsItem ? [allWidgetsItem, ...alphabeticalSort] : alphabeticalSort
		},
		brandFilterItems () {
			if (brandOptions.value.length >= 2) {
				return [{ value: null, text: 'All' }, ...brandOptions.value]
			}
			return brandOptions.value
		},
		showBrandFilter () {
			return brandOptions.value.length >= 2
		},
		showCookieFilter () {
			const dashboardConfig = this.dashboards.find(x => x.id === this.editableFilters.dashboardValue)
			return dashboardConfig?.enableCookieFilter ?? false
		},
		domainItems () {
			const items = []
			if ((this.domains?.length || 0) > 1) {
				items.push({ value: 'All', text: 'All', selected: true })
			}
			items.push(...this.domains.map(domain => ({
				value: domain,
				text: domain !== null ? domain : 'No Domain Captured'
			})))
			if (items.length === 1 && items[0].value !== 'All') {
				items[0].value = 'All' // Ensure 'All' is an option if only one domain exists
			}
			return items
		}
	},
	watch: {
		initialFilters: {
			handler (newFilters) {
				const defaultFilterStructure = {
					periodValue: this.APP_DEFAULT_PERIOD,
					widgetValue: -1,
					brandId: null,
					dashboardValue: null,
					startDate: null,
					endDate: null,
					domainValue: null
				}
				// Preserve the periodValue if it's explicitly set in newFilters, including Custom period
				const effectiveInitialFilters = { ...defaultFilterStructure, ...newFilters }

				// Special handling: if periodValue is explicitly provided, use it instead of default
				if (newFilters && newFilters.hasOwnProperty('periodValue')) {
					effectiveInitialFilters.periodValue = newFilters.periodValue
				}

				this.editableFilters = { ...effectiveInitialFilters }
				this.appliedFilters = { ...effectiveInitialFilters }
			},
			deep: true,
			immediate: true
		},
		initialPalette: {
			handler (newVal) {
				const paletteToSet = newVal || 'Default'
				this.editablePalette = paletteToSet
				this.appliedPalette = paletteToSet
			},
			immediate: true
		},
		editablePalette (newPalette, oldPalette) {
			if (newPalette !== oldPalette && newPalette !== this.appliedPalette) {
				this.appliedPalette = newPalette
				this.emitSettingsChange()
			}
		},
		editableFilters: {
			handler (newFilters, oldFilters) {
				if (!oldFilters || isEqual(this.appliedFilters, newFilters)) return
				this.appliedFilters = { ...newFilters }
				this.emitSettingsChange()
			},
			deep: true
		},
		'editableFilters.dashboardValue': 'handleDashboardValueChange'
	},
	methods: {
		...mapActions('dashboards', ['getDashboards']),
		/**
     * Handles changes to the dashboard value, fetching cookie domains if necessary.
     */
		async handleDashboardValueChange (newValue) {
			const dashboardConfig = this.dashboards.find(x => x.id === newValue)
			if (dashboardConfig?.enableCookieFilter) {
				await this.getCookieDomains()
			}
		},
		/**
     * Updates a specific filter value in the editableFilters object.
     * @param {string} key - The filter key to update.
     * @param {*} value - The new value for the filter.
     */
		updateFilter (key, value) {
			this.editableFilters = { ...this.editableFilters, [key]: value }
		},
		/**
     * Fetches cookie domains from the API and updates the local state.
     */
		async getCookieDomains () {
			try {
				const data = await getDomains()
				this.domains = data
			} catch (error) {
				console.error('Failed to load domains:', error)
				this.domains = [] // Ensure domains is an array even on error
			}
		},
		/**
     * Formats a date string for display (e.g., "MMM dd, yyyy").
     * @param {string | null} dateString - The date string to format.
     * @returns {string} The formatted date string or an empty string if input is invalid.
     */
		formatDate (dateString) {
			if (!dateString) return ''
			try {
				return format(new Date(dateString), 'MMM dd, yyyy')
			} catch (error) {
				console.warn('Invalid date string for formatting:', dateString, error)
				return dateString // Return original string if formatting fails
			}
		},
		/**
     * Emits an 'apply-settings' event with the current applied filters and palette.
     */
		emitSettingsChange () {
			this.$emit('apply-settings', {
				filters: { ...this.appliedFilters },
				palette: this.appliedPalette
			})
		},
		/**
     * Loads initial data required for the component, such as dashboards.
     */
		async loadInitialData () {
			try {
				await this.getDashboards({
					addCookieModule: false,
					submissionFragmentsEnabled: false
				})
			} catch (err) {
				console.error('Failed to load dashboards:', err)
			}

			await waitForLoad() // Wait for brands to be loaded
			// Initial call for cookie domains if needed based on current dashboard value
			await this.handleDashboardValueChange(this.editableFilters.dashboardValue)
		}
	},
	/**
   * Called when the component is created. Loads initial data.
   */
	async created () {
		await this.loadInitialData()
	}
}
